import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, SubTabs, Card, Btn, Chip, relTime, inputClass } from '../../components/admin/shell';

/**
 * PEOPLE › USER REPORTS — superadmin only, because a report names who complained about whom. Each
 * one shows what was reported and lets you decide: look into it, act, or dismiss with a note.
 * Accounts are suspended from USERS, events from EVENTS; this page links there.
 */
const TABS = [['waiting', 'WAITING'], ['actioned', 'ACTIONED'], ['dismissed', 'DISMISSED'], ['square', 'TOWN PORTALS']];
const REASON = { harassment: 'HARASSMENT', spam: 'SPAM', impersonation: 'IMPERSONATION', inappropriate: 'INAPPROPRIATE', safety: 'SAFETY', other: 'OTHER' };

function Person({ who }) {
  const name = who?.displayName || 'No name';
  return <Link to={`/admin/users/${who?.userId}`} className="font-bold underline">{name}{who?.handle ? ` @${who.handle}` : ''}</Link>;
}

function ReportCard({ report: r, onChanged }) {
  const { showAlert, showConfirm } = useDialog();
  const [note, setNote] = useState(r.moderatorNote || '');
  const [busy, setBusy] = useState(false);

  const run = async (call) => {
    setBusy(true);
    const res = await call();
    setBusy(false);
    if (res.success) onChanged();
    else showAlert({ title: 'ERROR', message: res.error || 'That did not save.', type: 'error' });
  };
  const decide = (status) => run(() => adminService.updateUserReport(r.id, { status, note }));
  const takeDown = async () => {
    const ok = await showConfirm({ title: `TAKE DOWN THIS ${r.story.kind === 'intro' ? 'INTRODUCTION' : 'STORY'}?`, message: 'It disappears for everyone and the report closes as actioned.', confirmLabel: 'TAKE IT DOWN', cancelLabel: 'KEEP IT', variant: 'danger' });
    if (ok) run(() => adminService.takeDownReported(r.id));
  };
  const open = r.status === 'open' || r.status === 'reviewing';

  return (
    <Card className="mt-3" data-testid="user-report">
      <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] font-bold tracking-[.14em]">
        <Chip>{REASON[r.reason] || r.reason?.toUpperCase()}</Chip>
        <Chip>{r.status.toUpperCase()}</Chip>
        <span className="text-[#6B7280]">{relTime(r.createdAt)}</span>
        {r.reportsAgainst > 1 && <span className="text-[#B91C1C]">{r.reportsAgainst} REPORTS AGAINST THEM</span>}
      </div>
      <p className="mt-3 font-mono text-[12px] leading-relaxed"><Person who={r.reporter} /> reported <Person who={r.reported} /></p>
      {r.details && <p className="mt-2 whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-[#374151]">{r.details}</p>}
      {r.message && <p className="mt-2 font-mono text-[12px] text-[#374151]"><b>MESSAGE · </b>{r.message.body || 'A view-once or media message. Not shown.'}</p>}
      {r.event && <p className="mt-2 font-mono text-[12px]"><b>{r.event.isPortal ? 'PORTAL' : 'EVENT'} · </b><Link className="underline" to={r.event.isPortal ? `/portals/${r.event.id}` : `/events/${r.event.id}`}>{r.event.title || 'Untitled'}</Link></p>}
      {r.story && <p className="mt-2 font-mono text-[12px]"><b>{r.story.kind === 'intro' ? 'INTRODUCTION' : 'STORY'} · </b>{r.story.live ? 'STILL UP' : 'ALREADY DOWN'}</p>}
      <textarea className={`${inputClass} mt-3 w-full`} rows={2} maxLength={2000} placeholder="Your note. Only superadmins see it." aria-label="Moderator note" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="mt-3 flex flex-wrap gap-2">
        {r.status === 'open' && <Btn variant="white" disabled={busy} onClick={() => decide('reviewing')}>REVIEWING</Btn>}
        {open && <Btn disabled={busy} onClick={() => decide('actioned')}>ACTIONED</Btn>}
        {open && <Btn variant="white" disabled={busy} onClick={() => decide('dismissed')}>DISMISS</Btn>}
        {!open && <Btn variant="white" disabled={busy} onClick={() => decide('open')}>REOPEN</Btn>}
        {r.story?.live && <Btn disabled={busy} onClick={takeDown}>TAKE IT DOWN</Btn>}
      </div>
    </Card>
  );
}

export default function UserReportsPage() {
  const [tab, setTab] = useState('waiting');
  const [loaded, setLoaded] = useState({ tab: null, rows: null });
  // The two tabs hold different rows, so only ever draw the ones this tab asked for.
  const rows = loaded.tab === tab ? loaded.rows : null;
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const r = tab === 'square' ? await adminService.getSquareReports() : await adminService.getUserReports(tab);
    if (r.success) setLoaded({ tab, rows: tab === 'square' ? r.data : r.data.items });
    else setError(r.error || 'Could not load reports.');
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <AdminPageHeader section="user-reports" viewOnly={false} meta="Answer within 24 hours. Both app stores expect it." />
      <SubTabs tabs={TABS.map(([key, label]) => ({ key, label }))} active={tab} onChange={setTab} />
      {error && <p className="mt-3 font-mono text-[12px] text-[#B91C1C]">{error}</p>}
      {rows && rows.length === 0 && <Card className="mt-3"><p className="font-mono text-[12px]">Nothing here.</p></Card>}
      {tab !== 'square' && (rows || []).map((r) => <ReportCard key={r.id} report={r} onChanged={load} />)}
      {tab === 'square' && (rows || []).map((r) => (
        <Card key={r.id} className="mt-3" data-testid="square-report">
          <p className="font-mono text-[12px] leading-relaxed">
            <b>{(r.category || '').toUpperCase()} · {(r.squareSlug || '').toUpperCase()}</b> · {relTime(r.createdAt)}<br />
            {r.reporter || 'Someone'} reported <Link className="underline" to={`/portals/${r.signalEventId}`}>{r.title || 'a Portal'}</Link>
            {r.note ? <><br />{r.note}</> : null}
          </p>
        </Card>
      ))}
    </>
  );
}
